import React, { useEffect, useState, useRef } from "react";
import CountUp from "react-countup";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import ExpenseDetailsModal from "../components/ExpenseDetailsModal";
import BudgetDashboard from "../components/BudgetDashboard";
import AutoTransactionManager from "../components/AutoTransactionManager";
import Header from "../components/Header";
import Layout from "../components/Layout";
import {
  fetchExpensesByCategory,
  fetchExpenseSummary,
  fetchInvestmentSummary,
  fetchIncomeSummary,
  fetchLastBalance,
  fetchBudgets,
  fetchAutoTransactions,
} from "../services/api";

const COLORS = [
  "#60A5FA",
  "#34D399",
  "#FBBF24",
  "#F87171",
  "#A78BFA",
  "#EC4899",
];

function BudgetPage() {
  const [expenses, setExpenses] = useState([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpense, setTotalExpense] = useState(0);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [projectedBalance, setProjectedBalance] = useState(0);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [trigger, setTrigger] = useState(0);
  const [showChart, setShowChart] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [totalBudget, setTotalBudget] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showPieChart, setShowPieChart] = useState(false);

  const expenseRef = useRef(0);
  const incomeRef = useRef(0);
  const projectedRef = useRef(0);

  // detect mobile layout for conditional animation
  useEffect(() => {
    const handleResize = () => setIsMobileLayout(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle data changes with smooth transition
  useEffect(() => {
    setIsTransitioning(true);
    setShowChart(false);
    setShowPieChart(false);
    const timer = setTimeout(() => {
      expenseRef.current = totalExpense;
      incomeRef.current = totalIncome;
      projectedRef.current = projectedBalance;
      setTrigger((prev) => prev + 1);
      setIsTransitioning(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [totalExpense, totalIncome, projectedBalance]);

  const loadData = async (y, m) => {
    const grouped = await fetchExpensesByCategory(y, m);
    const summaryExpense = await fetchExpenseSummary(y, m);
    const summaryInvestment = await fetchInvestmentSummary(y, m);
    const summaryIncome = await fetchIncomeSummary(y, m);
    const latestBalance = await fetchLastBalance();
    const current = latestBalance ? latestBalance.balance_amt : 0;
    const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

    let projectedAvailable = 0;

    grouped.forEach((cat) => {
      if (cat.expense_amt <= cat.budget_amt) {
        projectedAvailable -= cat.budget_amt - cat.expense_amt;
      }
    });

    const autoTransactions = await fetchAutoTransactions();
    const today = new Date();
    const currentDay = today.getDate();
    const lastDayOfMonth = getLastDayOfMonth(today.getFullYear(), today.getMonth());

    autoTransactions.forEach((t) => {
      const { transaction_type, transaction_amount, day_of_month } = t;

      if (transaction_type === "income") {
        const effectiveDay =
          day_of_month > lastDayOfMonth ? lastDayOfMonth : day_of_month;

        if (currentDay < effectiveDay) {
          projectedAvailable += transaction_amount;
        }
      }
    });

    let totalBudgetAmt = 0;
    try {
      const budgets = await fetchBudgets();
      if (budgets && budgets.length > 0) {
        totalBudgetAmt = budgets.reduce((sum, b) => sum + (b.budget_amt || 0), 0);
      }
    } catch (err) {
      console.error("Error fetching budgets:", err);
    }

    const projection = current + projectedAvailable;
    setExpenses(grouped);
    setTotalExpense(summaryExpense.length > 0 ? summaryExpense[0].total_expense_amt : 0);
    setTotalInvestment(summaryInvestment.length > 0 ? summaryInvestment[0].total_investment_amt : 0);
    setProjectedBalance(projection);
    setTotalIncome(summaryIncome.length > 0 ? summaryIncome[0].total_income_amt : 0);
    setTotalBudget(totalBudgetAmt);
  };

  useEffect(() => {
    loadData(year, month);
  }, [year, month]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handleAllCountUpsDone = () => {
    setTimeout(() => {
      setShowChart(true);
      if (isMobileLayout && carouselIndex === 1) {
        setShowPieChart(true);
      } else if (!isMobileLayout) {
        setShowPieChart(true);
      }
    }, 200);
  };

  useEffect(() => {
    if (isMobileLayout && carouselIndex === 1 && showChart) {
      setShowPieChart(true);
    } else if (isMobileLayout && carouselIndex === 0) {
      setShowPieChart(false);
    }
  }, [carouselIndex, isMobileLayout, showChart]);

  const THRESHOLD_PERCENT = 0.03;
  const totalExpenses = expenses.reduce((sum, e) => sum + e.expense_amt, 0);

  const pieData = [];
  let otherSum = 0;
  expenses.forEach((e) => {
    if (totalExpenses === 0 || e.expense_amt / totalExpenses < THRESHOLD_PERCENT) {
      otherSum += e.expense_amt;
    } else {
      pieData.push({ name: e.expense_category_name, value: e.expense_amt });
    }
  });
  if (otherSum > 0) pieData.push({ name: "Other", value: otherSum });

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 shadow-md">
          <p className="font-semibold">{data.name}</p>
          <p className="text-green-400 font-medium">${data.value.toLocaleString()}</p>
          <p className="text-gray-400 text-xs">
            {((data.value / totalExpenses) * 100).toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };
{/* Define cashFlow variable */}
const cashFlow = totalIncome - totalExpense - totalInvestment

  return (
    <Layout>
      <div
        className="flex flex-col items-center justify-between relative"
        style={{
          minHeight: "100svh",
          padding: "max(1rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom)) 1rem",
          boxSizing: "border-box",
        }}
      >
        <div className="w-full flex items-center justify-between relative">
          <Header />
        </div>

        <div className="w-full flex justify-end mt-6 sm:mt-4 pr-6">
          <Link
            to="/balance/history"
            className="group flex items-center gap-2 text-blue-400 hover:text-blue-300 
                   font-[Poppins] transition-all duration-300 opacity-0 translate-x-[-20px] animate-slide-in"
          >
            Balance History
            <ArrowRight
              size={22}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>

        {/* Monthly Overview - Desktop Only */}
        <h1 className="hidden md:block text-3xl md:text-4xl font-extrabold text-white mb-6 mt-10 sm:mt-16">
          Monthly Overview
        </h1>

        {/* Month/Year Selector */}
        <div className="flex flex-row items-center gap-3 sm:gap-4 mb-8 w-full sm:w-auto mt-8 md:mt-0 justify-center">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-3 py-2 rounded-lg bg-gray-800 text-gray-200 border border-gray-700 focus:ring-2 focus:ring-blue-400"
          >
            {months.map((m, i) => (
              <option key={i + 1} value={i + 1}>
                {m}
              </option>
            ))}
          </select>

          <input
            type="number"
            value={year}
            min="2000"
            max="2100"
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-24 px-3 py-2 rounded-lg bg-gray-800 text-gray-200 border border-gray-700 focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Main Section */}
        <motion.div
          layout
          className="flex flex-col md:flex-row items-center justify-center gap-10 w-full"
          transition={{ duration: 0.8, ease: "easeInOut" }}
        >
          {/* Mobile Carousel */}
          {isMobileLayout ? (
            <div className="w-full max-w-md relative">
              {/* Carousel Navigation */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <button
                  onClick={() => setCarouselIndex(0)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${carouselIndex === 0
                    ? "bg-blue-500 text-white"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                    }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setCarouselIndex(1)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${carouselIndex === 1
                    ? "bg-blue-500 text-white"
                    : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                    }`}
                >
                  Chart
                </button>
              </div>

              {/* Carousel Content */}
              <div className="relative overflow-hidden">
                <AnimatePresence mode="wait">
                  {carouselIndex === 0 ? (
                    <motion.div
                      key="summary"
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      transition={{ duration: 0.3 }}
                      className="text-center space-y-8 px-4 sm:px-6 max-w-full break-words"
                    >
                      {/* CashFlow */}
                      <div className="border-t border-gray-700 my-4 w-2/3 mx-auto opacity-60"></div>
                      <div>
                        <h4 className="text-xl font-semibold text-gray-300 tracking-wide">
                          CashFlow
                        </h4>
                        <p className="text-5xl sm:text-6xl font-extrabold text-purple-400 mt-2 leading-none">
                          <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                            {!isTransitioning && (
                              <CountUp
                                key={`cashflow-${trigger}`}
                                start={0}
                                end={cashFlow}
                                duration={1.5}
                                decimals={2}
                                prefix="$"
                                onEnd={handleAllCountUpsDone}
                              />
                            )}
                          </span>
                        </p>
                      </div>

                      <div className="border-t border-gray-700 my-4 w-2/3 mx-auto opacity-60"></div>

                      {/* Total Expenses */}
                      <div>
                        <h3 className="text-xl font-semibold text-gray-300 tracking-wide">
                          Total Expenses
                        </h3>
                        <p className="text-4xl sm:text-5xl font-extrabold text-red-400 mt-2 leading-none">
                          <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                            {!isTransitioning && (
                              <CountUp
                                key={`expense-${trigger}`}
                                start={0}
                                end={totalExpense}
                                duration={1.5}
                                decimals={2}
                                prefix="$"
                              />
                            )}
                          </span>
                        </p>
                      </div>

                      <div className="border-t border-gray-700 my-4 w-2/3 mx-auto opacity-60"></div>

                      {/* Total Income */}
                      <div>
                        <h3 className="text-xl font-semibold text-gray-300 tracking-wide">
                          Total Income
                        </h3>
                        <p className="text-4xl sm:text-5xl font-extrabold text-green-400 mt-2 leading-none">
                          <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                            {!isTransitioning && (
                              <CountUp
                                key={`income-${trigger}`}
                                start={0}
                                end={totalIncome}
                                duration={1.5}
                                decimals={2}
                                prefix="$"
                              />
                            )}
                          </span>
                        </p>
                      </div>

                      <div className="border-t border-gray-700 my-4 w-2/3 mx-auto opacity-60"></div>

                      {/* Total Investment */}
                      <div>
                        <h3 className="text-xl font-semibold text-gray-300 tracking-wide">
                          Total Investment
                        </h3>
                        <p className="text-4xl sm:text-5xl font-extrabold text-yellow-400 mt-2 leading-none">
                          <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                            {!isTransitioning && (
                              <CountUp
                                key={`investment-${trigger}`}
                                start={0}
                                end={totalInvestment}
                                duration={1.5}
                                decimals={2}
                                prefix="$"
                              />
                            )}
                          </span>
                        </p>
                      </div>

                      <div className="border-t border-gray-700 my-4 w-2/3 mx-auto opacity-60"></div>

                      {/* Allocated Budget */}
                      <div>
                        <h4 className="text-xl font-semibold text-gray-300 tracking-wide">
                          Allocated Budget
                        </h4>
                        <p className="text-4xl sm:text-5xl font-extrabold text-blue-400 mt-2 leading-none">
                          <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                            {!isTransitioning && (
                              <CountUp
                                key={`budget-${trigger}`}
                                start={0}
                                end={totalBudget}
                                duration={1.5}
                                decimals={2}
                                prefix="$"
                              />
                            )}
                          </span>
                        </p>
                      </div>

                      <div className="border-t border-gray-700 my-4 w-2/3 mx-auto opacity-60"></div>

                      {/* Projected Balance */}
                      {month === new Date().getMonth() + 1 &&
                        year === new Date().getFullYear() && (
                          <div className="pt-4">
                            <p className="text-md text-gray-500">
                              Projected Balance:{" "}
                              <span
                                className={`inline-block min-w-[8ch] tabular-nums font-[Poppins] font-semibold text-2xl ${projectedBalance >= 0 ? "text-green-400" : "text-red-400"
                                  }`}
                              >
                                {!isTransitioning && (
                                  <CountUp
                                    key={`projected-${trigger}`}
                                    start={0}
                                    end={projectedBalance}
                                    duration={1.8}
                                    decimals={2}
                                    prefix="$"
                                  />
                                )}
                              </span>
                            </p>
                          </div>
                        )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="chart"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 50 }}
                      transition={{ duration: 0.3 }}
                      className="w-full select-none flex flex-col items-center pb-8"
                    >
                      {pieData.length === 0 ? (
                        <div className="w-full h-full flex items-center justify-center py-20">
                          <div className="text-center">
                            <div className="text-4xl sm:text-6xl mb-4 opacity-30">📊</div>
                            <p className="text-gray-400 text-lg font-[Poppins]">
                              No expense data available
                            </p>
                            <p className="text-gray-500 text-sm mt-2 font-[Poppins]">
                              Add some expenses to see the breakdown
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius="70%"
                                isAnimationActive={showPieChart}
                                labelLine={false}
                                label={false}
                                style={{ outline: "none" }}
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip content={<CustomPieTooltip />} />
                            </PieChart>
                          </ResponsiveContainer>

                          {/* Custom Legend for Mobile */}
                          <div className="w-full max-w-xs flex flex-col mt-6 space-y-3 px-4">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-gray-300 font-semibold text-sm uppercase tracking-wide">
                                CashOut Breakdown
                              </h3>
                              <button
                                className="text-gray-400 hover:text-blue-400 transition-colors duration-200"
                                onClick={() => setShowModal(true)}
                                aria-label="View expense details"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                                  />
                                </svg>
                              </button>
                            </div>

                            {pieData.map((entry, index) => (
                              <div
                                key={`mobile-legend-${index}`}
                                className="flex items-center gap-3 py-1"
                              >
                                <span
                                  className="inline-block w-4 h-4 rounded-sm flex-shrink-0"
                                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                />
                                <span className="text-sm text-gray-200 text-left flex-1">
                                  {entry.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Layout */}
              <motion.div
                layout
                className="text-center lg:text-left space-y-8 px-4 sm:px-6 md:px-8 max-w-full break-words"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: isTransitioning ? 0 : 1,
                  x: isTransitioning ? 0 : showChart ? -50 : 0,
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
              >
                {/* CashFlow */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-300 tracking-wide">
                    CashFlow
                  </h3>
                  <p className="text-5xl sm:text-6xl font-extrabold text-purple-400 mt-2 leading-none">
                    <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                      {!isTransitioning && (
                        <CountUp
                          key={`cashflow-${trigger}`}
                          start={0}
                          end={cashFlow}
                          duration={1.5}
                          decimals={2}
                          prefix="$"
                          onEnd={handleAllCountUpsDone}
                        />
                      )}
                    </span>
                  </p>
                </div>

                <div className="border-t border-gray-700 my-4 w-2/3 mx-auto lg:mx-0 opacity-60"></div>

                {/* Total Expenses */}
                <div>
                  <h4 className="text-xl font-semibold text-gray-300 tracking-wide">
                    Total Expenses
                  </h4>
                  <p className="text-4xl sm:text-5xl font-extrabold text-red-400 mt-2 leading-none">
                    <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                      {!isTransitioning && (
                        <CountUp
                          key={`expense-${trigger}`}
                          start={0}
                          end={totalExpense}
                          duration={1.5}
                          decimals={2}
                          prefix="$"
                        />
                      )}
                    </span>
                  </p>
                </div>

                <div className="border-t border-gray-700 my-4 w-2/3 mx-auto lg:mx-0 opacity-60"></div>

                {/* Total Income */}
                <div>
                  <h4 className="text-xl font-semibold text-gray-300 tracking-wide">
                    Total Income
                  </h4>
                  <p className="text-4xl sm:text-5xl font-extrabold text-green-400 mt-2 leading-none">
                    <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                      {!isTransitioning && (
                        <CountUp
                          key={`income-${trigger}`}
                          start={0}
                          end={totalIncome}
                          duration={1.5}
                          decimals={2}
                          prefix="$"
                        />
                      )}
                    </span>
                  </p>
                </div>

                <div className="border-t border-gray-700 my-4 w-2/3 mx-auto lg:mx-0 opacity-60"></div>

                {/* Total Investment */}
                <div>
                  <h4 className="text-xl font-semibold text-gray-300 tracking-wide">
                    Total Investment
                  </h4>
                  <p className="text-4xl sm:text-5xl font-extrabold text-yellow-400 mt-2 leading-none">
                    <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                      {!isTransitioning && (
                        <CountUp
                          key={`investment-${trigger}`}
                          start={0}
                          end={totalInvestment}
                          duration={1.5}
                          decimals={2}
                          prefix="$"
                        />
                      )}
                    </span>
                  </p>
                </div>

                <div className="border-t border-gray-700 my-4 w-2/3 mx-auto lg:mx-0 opacity-60"></div>

                {/* Allocated Budget */}
                <div>
                  <h4 className="text-xl font-semibold text-gray-300 tracking-wide">
                    Allocated Budget
                  </h4>
                  <p className="text-4xl sm:text-5xl font-extrabold text-blue-400 mt-2 leading-none">
                    <span className="inline-block min-w-[8ch] tabular-nums font-[Poppins]">
                      {!isTransitioning && (
                        <CountUp
                          key={`budget-${trigger}`}
                          start={0}
                          end={totalBudget}
                          duration={1.5}
                          decimals={2}
                          prefix="$"
                        />
                      )}
                    </span>
                  </p>
                </div>

                <div className="border-t border-gray-700 my-4 w-2/3 mx-auto lg:mx-0 opacity-60"></div>

                {/* Projected Balance — only for current month */}
                {month === new Date().getMonth() + 1 &&
                  year === new Date().getFullYear() && (
                    <div className="pt-4">
                      <p className="text-md text-gray-500">
                        Projected Balance:{" "}
                        <span
                          className={`inline-block min-w-[8ch] tabular-nums font-[Poppins] font-semibold text-2xl ${projectedBalance >= 0 ? "text-green-400" : "text-red-400"
                            }`}
                        >
                          {!isTransitioning && (
                            <CountUp
                              key={`projected-${trigger}`}
                              start={0}
                              end={projectedBalance}
                              duration={1.8}
                              decimals={2}
                              prefix="$"
                            />
                          )}
                        </span>
                      </p>
                    </div>
                  )}
              </motion.div>

              {/* Pie Chart */}
              <AnimatePresence>
                {showChart && (
                  <motion.div
                    key="piechart"
                    layout
                    className="w-full max-w-sm sm:max-w-md md:max-w-lg select-none mb-10 sm:mb-0 flex flex-col items-center"
                    initial={{ opacity: 0, scale: 0.8, x: 50 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  >
                    {pieData.length === 0 ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl sm:text-6xl mb-4 opacity-30">📊</div>
                          <p className="text-gray-400 text-lg font-[Poppins]">
                            No expense data available
                          </p>
                          <p className="text-gray-500 text-sm mt-2 font-[Poppins]">
                            Add some expenses to see the breakdown
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius="80%"
                              isAnimationActive={showPieChart}
                              labelLine={false}
                              label={false}
                              style={{ outline: "none" }}
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={index} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomPieTooltip />} />
                            <Legend
                              layout="vertical"
                              verticalAlign="middle"
                              align="right"
                              wrapperStyle={{
                                maxHeight: 300,
                                overflowY: "auto",
                                paddingRight: 10,
                                paddingLeft: 20,
                                color: "#ccc",
                                cursor: "pointer",
                              }}
                              content={({ payload }) => (
                                <div className="flex flex-col justify-center text-sm text-gray-200 space-y-2 w-40">
                                  {payload.map((entry, index) => (
                                    <div
                                      key={`legend-item-${index}`}
                                      className="flex items-start gap-2 transition-colors duration-200 cursor-pointer"
                                      onClick={() => setShowModal(true)}
                                    >
                                      <span
                                        className="inline-block w-3 h-3 mt-1 rounded-sm flex-shrink-0"
                                        style={{ backgroundColor: entry.color }}
                                      />
                                      <span
                                        className="break-words text-gray-300 hover:text-blue-400 leading-tight transition-colors duration-200"
                                        style={{ wordBreak: "break-word", whiteSpace: "normal", maxWidth: "8rem" }}
                                      >
                                        {entry.value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}

          {showModal && (
            <ExpenseDetailsModal
              expenses={expenses}
              onClose={() => setShowModal(false)}
            />
          )}
        </motion.div>

        <div className="mt-16 sm:mt-20">
          <BudgetDashboard onBudgetsChanged={() => loadData(year, month)} />
        </div>
        <div className="mt-16 sm:mt-20">
          <AutoTransactionManager onAutoTransactionsChanged={() => loadData(year, month)} />
        </div>
      </div>
    </Layout>
  );
}

export default BudgetPage;